import { useEffect, useState } from "react";
import axios from "axios";
import { Receipt, FileText, IndianRupee } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** One short line per charge — same wording as WhatsApp and web chat. */
export function ChargeLines({ charges, baseLabel = "Order amount", testid }) {
  if (!charges) return null;
  const rows = [
    [baseLabel, charges.base], ["Convenience fee (3%)", charges.convenience_fee],
    ["Processing (1.95%)", charges.processing_fee],
    [`GST (18% on fees${charges.gst_type === "IGST" ? ", IGST" : ", CGST + SGST"})`, charges.gst],
  ];
  return (
    <dl className="mt-2 space-y-1 text-base" data-testid={testid}>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4"><dt className="text-gray-600">{k}</dt><dd className="tabular-nums">{inr(v)}</dd></div>
      ))}
      <div className="flex justify-between gap-4 font-bold text-[#1A172E] border-t border-gray-200 pt-1.5">
        <dt>Total</dt><dd className="tabular-nums">{inr(charges.grand_total)}</dd>
      </div>
    </dl>
  );
}

function MonthCharges({ month }) {
  return (
    <div className="rounded-xl bg-[#F4F3FA] border border-[#E3E1F2] p-4 mb-4" data-testid="charges-this-month">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Charges this month</p>
      <p className="text-2xl font-bold text-[#1A172E] mt-1 tabular-nums" data-testid="charges-this-month-total">{inr(month.charges_total)}</p>
      <p className="text-sm text-gray-600 mt-1">
        Convenience {inr(month.convenience_fee)} · Processing {inr(month.processing_fee)} · GST {inr(month.gst)} · {month.count} order{month.count === 1 ? "" : "s"}
      </p>
    </div>
  );
}

const STATUS = { paid: "Paid", refunded: "Refunded", refund_failed: "Refund pending", pending_settlement: "Confirming", debited: "Confirming", pending_debit: "Confirming" };

/** Payment history with every charge, GST and the invoice link. */
export function PaymentsCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/family/payments`).then((r) => setData(r.data)).catch(() => setData({ payments: [] }));
  }, []);
  if (!data) return null;
  const { payments, charges_this_month: month } = data;
  if (!payments.length && !month) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="payments-card">
      <p className="font-semibold text-[#1A172E] flex items-center gap-2 mb-3"><Receipt className="w-5 h-5 text-[#2D2766]" /> Payments &amp; charges</p>
      {month && <MonthCharges month={month} />}
      {!payments.length && <p className="text-base text-gray-400 flex items-center gap-2"><IndianRupee className="w-4 h-4" /> No payments yet</p>}
      <ul className="space-y-4">
        {payments.map((p) => (
          <li key={p.id} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0" data-testid={`payment-row-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-[#1A172E]">{p.title}</p>
                <p className="text-sm text-gray-500">{new Date(p.created_at).toLocaleString("en-IN")} · {STATUS[p.status] || p.status}</p>
              </div>
              {p.invoice && (
                <a href={p.invoice.url} target="_blank" rel="noreferrer" data-testid={`invoice-link-${p.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-[#2D2766] hover:underline">
                  <FileText className="w-4 h-4" /> Invoice
                </a>
              )}
            </div>
            {p.charges ? <ChargeLines charges={p.charges} baseLabel={p.kind === "bill" ? "Bill amount" : "Food (paid to Swiggy)"} testid={`payment-charges-${p.id}`} />
              : <p className="text-base mt-1 tabular-nums">{inr(p.total)}</p>}
          </li>
        ))}
      </ul>
    </div>
  );
}
